export type AdjudicatorMessageSections = {
  caseSummary: string;
  reviewMeasures: string;
  followUpEvidence: string;
};

const FOLLOW_UP_RE =
  /\b(request|ask for|ask the applicant|obtain|follow-up|follow up|pay stub|paystub|employment contract|offer letter|documents? to request|evidence to request|letter confirming|written explanation|professional profile|trade licence|apprenticeship record)\b/i;

const MEASURES_RE =
  /\b(confirm|verify|validate|reconcile|rule out|before deciding|should verify|check that|ensure the|clarify|measures)\b/i;

function labeledSection(
  text: string,
  label: RegExp,
): { body: string; rest: string } | null {
  const match = text.match(label);
  if (!match?.index && match?.index !== 0) return null;
  const start = match.index + match[0].length;
  const rest = text.slice(start).trim();
  const nextLabel = rest.search(
    /\b(review measures|follow-up evidence|follow up evidence|follow-up|measures to take)\b/i,
  );
  const body = (nextLabel > 0 ? rest.slice(0, nextLabel) : rest).trim();
  return { body, rest: nextLabel > 0 ? rest.slice(nextLabel).trim() : '' };
}

/** Split AI creditAdjudicatorMessage into case summary / review measures / follow-up. */
export function parseCreditAdjudicatorMessage(
  raw: string | null | undefined,
): AdjudicatorMessageSections {
  const text = raw?.trim() ?? '';
  if (!text) {
    return { caseSummary: '', reviewMeasures: '', followUpEvidence: '' };
  }

  const explicitCase = labeledSection(
    text,
    /case summary\s*[—:-]\s*/i,
  );
  if (explicitCase?.body) {
    const measures = labeledSection(
      explicitCase.rest,
      /review measures\s*[—:-]\s*/i,
    );
    const followUp = measures
      ? labeledSection(
          measures.rest,
          /follow-up evidence\s*[—:-]\s*/i,
        ) ?? labeledSection(measures.rest, /follow up evidence\s*[—:-]\s*/i)
      : labeledSection(
          explicitCase.rest,
          /follow-up evidence\s*[—:-]\s*/i,
        );

    return {
      caseSummary: explicitCase.body.replace(/\s+/g, ' ').trim(),
      reviewMeasures: (measures?.body ?? '').replace(/\s+/g, ' ').trim(),
      followUpEvidence: (followUp?.body ?? measures?.rest ?? '')
        .replace(/\s+/g, ' ')
        .trim(),
    };
  }

  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (sentences.length === 0) {
    return { caseSummary: text, reviewMeasures: '', followUpEvidence: '' };
  }

  if (sentences.length === 1) {
    return { caseSummary: sentences[0]!, reviewMeasures: '', followUpEvidence: '' };
  }

  let followUpStart = sentences.findIndex((s) => FOLLOW_UP_RE.test(s));
  if (followUpStart < 0 && sentences.length >= 2) {
    followUpStart = sentences.length - 1;
    if (!FOLLOW_UP_RE.test(sentences[followUpStart]!) && !MEASURES_RE.test(sentences[followUpStart]!)) {
      followUpStart = -1;
    }
  }

  if (followUpStart > 0) {
    return {
      caseSummary: sentences[0]!,
      reviewMeasures: sentences.slice(1, followUpStart).join(' '),
      followUpEvidence: sentences.slice(followUpStart).join(' '),
    };
  }

  if (sentences.length === 2) {
    const second = sentences[1]!;
    if (MEASURES_RE.test(second) || FOLLOW_UP_RE.test(second)) {
      return {
        caseSummary: sentences[0]!,
        reviewMeasures: second,
        followUpEvidence: '',
      };
    }
  }

  if (sentences.length >= 3) {
    return {
      caseSummary: sentences[0]!,
      reviewMeasures: sentences.slice(1, -1).join(' '),
      followUpEvidence: sentences[sentences.length - 1]!,
    };
  }

  return {
    caseSummary: sentences.join(' '),
    reviewMeasures: '',
    followUpEvidence: '',
  };
}
